import type { FC, ReactNode } from 'react'

import CurrentThemeProvider from 'components/CurrentThemeProvider'
import MenuProvider from 'components/MenuProvider'
import GlobalStyles from 'components/GlobalStyles'
import AssetProvider from 'components/AssetProvider'
import IsDesktopFooterVisibleProvider from './IsDesktopFooterVisibleProvider'
import CurrentSectionProvider from 'components/RedesignJourney/providers/CurrentSectionProvider'
import VariantLoadProvider from 'components/VariantLoadProvider'
import BottleProductVariantProvider from 'components/BottleProductVariantProvider'
import InventoryProvider from 'components/InventoryProvider'
import StorefrontProvider from 'components/StorefrontProvider'
import { AudioProvider } from 'components/AudioProvider'
import { AudioRouteCoordinator } from 'components/AudioRouteCoordinator'
import ThemePerfProfiler from 'components/ThemePerfProfiler'

import type { VariantSlug } from 'data/product'
import Background from 'components/Background'

interface ProvidersProps {
  backgroundEnabled?: boolean
  initialVariant?: VariantSlug
  children: ReactNode
}

const CommerceProviders: FC<{
  children: ReactNode
}> = ({ children }) => (
  <InventoryProvider>
    <StorefrontProvider>{children}</StorefrontProvider>
  </InventoryProvider>
)

const StyleProviders: FC<{
  backgroundEnabled?: boolean
  children: ReactNode
}> = ({ backgroundEnabled, children }) => (
  <>
    <GlobalStyles />
    <CurrentThemeProvider>
      <ThemePerfProfiler>
        {/* use background component to prevent white flash between routes landing page and meet the bottle page */}
        {backgroundEnabled ? <Background /> : null}
        {children}
      </ThemePerfProfiler>
    </CurrentThemeProvider>
  </>
)

const AudioExperienceProviders: FC<{
  children: ReactNode
}> = ({ children }) => (
  <AudioProvider>
    <AudioRouteCoordinator />
    {children}
  </AudioProvider>
)

const PageStateProviders: FC<{
  initialVariant?: VariantSlug
  children: ReactNode
}> = ({ initialVariant, children }) => (
  <BottleProductVariantProvider initialVariant={initialVariant}>
    <MenuProvider>
      <AssetProvider>
        <CurrentSectionProvider>
          <VariantLoadProvider>
            <IsDesktopFooterVisibleProvider>
              {children}
            </IsDesktopFooterVisibleProvider>
          </VariantLoadProvider>
        </CurrentSectionProvider>
      </AssetProvider>
    </MenuProvider>
  </BottleProductVariantProvider>
)

const PageProviders: FC<ProvidersProps> = ({
  backgroundEnabled,
  initialVariant,
  children,
}) => (
  <CommerceProviders>
    <PageStateProviders initialVariant={initialVariant}>
      <StyleProviders backgroundEnabled={backgroundEnabled}>
        <AudioExperienceProviders>{children}</AudioExperienceProviders>
      </StyleProviders>
    </PageStateProviders>
  </CommerceProviders>
)

export default PageProviders
